/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Motion, spring } from 'react-motion';
import styled from 'styled-components';
import range from 'lodash.range';

const CYCLE_DURATION = 10000;

const FullScreen = styled.div`
    position: ${(props) => props.position};
    z-index: ${(props) => (props.position === 'relative' ? 0 : 1)};
    top: 0;
    width: 100%;
    height: 100%;
    ${(props) => (Number.isInteger(props.left) ? `left: ${props.left}px;` : '')};
`;

const PlayPauseWrapper = styled.div`
  outline: none;
  border: 0px solid;
  background: transparent;
  position: absolute;
  top: 5px;
  right: 5px;
  width: 60px;
  z-index: 2;
  text-align: center;
`;

const NiceButton = styled.button`
  border: 2px solid ${(props) => (props.theme.gray)};
  background: rgba(255, 0, 0, 0);
  border-radius: 20px;
  fill: ${(props) => (props.theme.gray)};
  &:focus {
    outline: none;
  }
  &:hover {
    border: 2px solid ${(props) => (props.theme.grayLight)};
    cursor: pointer;
    fill: ${(props) => (props.theme.grayLight)};
  }
`;

const HotLink = styled(Link)`
  font-size: 12px;
  color: ${(props) => props.theme.slingBlueDark} !important;
  &:visited {
    color: ${(props) => props.theme.slingBlueDark} !important;
  }
  &:hover {
    color: ${(props) => props.theme.slingBluer} !important;
  }
`;

const PLAY = 'M11,10 L18,13.74 18,22.28 11,26 M18,13.74 L26,18 26,18 18,22.28';
const PAUSE = 'M11,10 L17,10 17,26 11,26 M20,10 L26,10 26,26 20,26';

const PlayPauseButton = ({ onClick, flip, link }) => (
  <>
    <NiceButton onClick={onClick}>
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 36 36"
        version="1.1"
      >
        <path d={flip ? PLAY : PAUSE} />
      </svg>
    </NiceButton>
    {
      !flip ? '' : <HotLink to={link}>LINK</HotLink>
    }
  </>
);

PlayPauseButton.propTypes = { flip: PropTypes.bool.isRequired, onClick: PropTypes.func.isRequired, link: PropTypes.string.isRequired };

/*
NOTE: This class will scroll N dashboards by creating N+1 components. Dashboard 0 gets two components (duplicates).
Components are laid out horizontally across a div (window.width * (N+1)) pixels wide.
The first pane's extra copy is at the rightmost end.

We smoothly scroll the div to the left and pause the viewport each dashboard. At the very end the viewport is over the
very last pane (the duplicate of pane 0).

We need to do it this way because the very first (leftmost) dashboard needs to show up at the right as we scroll off the
rightmost dashboard (pane N). We create a duplicate just for that one animation.

At the end of the animation, the div is immediately repositioned over the first pane, which appears identical.

The animation of the very first cycle looks a little screwed up because we're trying to establish synchronicity
with any other Scrollers on the monitors in the ops room that are showing analogous dashboards.
For example RSDVR playbacks and RSDVR recordings stay in sync, both showing GP2, then SJC1, then DC10.
Scroll transitions happen simultaneously using the system clock across both machines.
*/
class Scroller extends Component {

  state = {
    frameWidth: window.innerWidth,
    flip: false,
  };

  constructor(props) {
    super(props);
    this.mounted = false;
    this.cycleDurationMilliseconds = props.cycleDurationMilliseconds ? props.cycleDurationMilliseconds : CYCLE_DURATION;
    this.renderCount = 0;
    this.childRefs = [];
    window.addEventListener('resize', () => {
      if (this.mounted) {
        this.setState({ frameWidth: Math.max(this.props.minWidth, window.innerWidth) });
      }
    });
  }

  componentDidMount() {
    this.mounted = true;
    setTimeout(() => {
      if (this.mounted) {
        const startTime = Date.now();
        let counter = 0;
        const doItAndSetNextTimer = () => {
          if (this.mounted) {
            counter++;
            const nextTime = startTime + (counter * this.cycleDurationMilliseconds);
            setTimeout(doItAndSetNextTimer, nextTime - Date.now());
            const { flip } = this.state;
            if (!flip) {
              this.renderCount++;
              this.setState({ frameWidth: Math.max(this.props.minWidth, window.innerWidth) });
            }
          }
        };
        doItAndSetNextTimer();
      }
    }, this.cycleDurationMilliseconds - (Date.now() % this.cycleDurationMilliseconds));
    this.setState({ frameWidth: Math.max(this.props.minWidth, window.innerWidth) });
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  render() {
    const { frameWidth, flip } = this.state;

    const { children, links } = this.props;

    const childPanes = React.Children.toArray(children);
    if (childPanes.length === 0) {
      console.log('WARNING: Scroller has no screens to show.');
      return <FullScreen position="relative" />;
    }
    if (childPanes.length === 1) {
      // eslint-disable-next-line no-console
      console.log('WARNING: Scroller has only one screen.');
      return (
        <FullScreen postion="absolute">
          {childPanes[0]}
        </FullScreen>
      );
    }

    const items = range(childPanes.length).map((e, i) => ({
      index: e,
      key: `${links[e]}-pane${i}`,
      component: childPanes[e],
    }));
    items.push({
      index: childPanes.length,
      key: `${links[0]}-pane${childPanes.length}`,
      component: childPanes[0],
    });

    const currentChildIndex = (this.renderCount + 1) % childPanes.length;
    const currentOffset = frameWidth * currentChildIndex;
    const finalOffset = currentOffset + frameWidth;
    let motionComponent = null;
    const cycleDurationMilliseconds = this.props.cycleDurationMilliseconds;

    this.childRefs.forEach((ref, index) => {
      // Inject "scrollerPaneVisible" and "cycleDurationMilliseconds" properties into dashboard
      // in case it needs it for timing animations, etc.
      setTimeout(() => {
        ref.setState({
          cycleDurationMilliseconds,
          scrollerPaneVisible: ((index === (currentChildIndex + 1) % childPanes.length)
            || (index === childPanes.length && currentChildIndex === 0)), // For the spare dashboard at the end
        });
      });
    });

    return (
      <FullScreen position="relative">
        <PlayPauseWrapper>
          <PlayPauseButton
            flip={flip}
            onClick={(_e) => {
              this.setState({ flip: !flip });
            }}
            link={links[(this.renderCount + 1) % childPanes.length]}
          />
        </PlayPauseWrapper>
        <Motion
          defaultStyle={{ left: currentOffset }}
          style={{ left: spring(finalOffset) }}
          ref={(mp) => { motionComponent = mp; }}
          onRest={
            () => {
              if (motionComponent && finalOffset >= frameWidth * childPanes.length) {
                motionComponent.setState({
                  currentStyle: { left: 0 },
                  lastIdealStyle: { left: 0 },
                });
              }
            }
          }
        >
          {
            (interpolatedStyle) => (
              <>
                {
                items.map(({ index, key, component }) => (
                  <FullScreen
                    position="absolute"
                    key={key}
                    left={Math.round((frameWidth * index) - interpolatedStyle.left)}
                  >
                    {
                      React.cloneElement(component, {
                        key: `Cloned-${key}`,
                        ref: (el) => {
                          this.childRefs[index] = el;
                        },
                      })
                    }
                  </FullScreen>
                ))
              }
              </>
            )
          }
        </Motion>
      </FullScreen>
    );
  }
}

// https://github.com/yannickcr/eslint-plugin-react/issues/7
Scroller.propTypes = ({
  links: PropTypes.arrayOf(PropTypes.string),
  minWidth: PropTypes.number,
  cycleDurationMilliseconds: PropTypes.number,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
});

Scroller.defaultProps = ({
  links: [],
  minWidth: 1280,
  cycleDurationMilliseconds: CYCLE_DURATION,
  children: [],
});

export default Scroller;
